// AGC: MachineCard - Cream-style card with category icon
// AGC: Left side: status, name, category/brand, hours
// AGC: Right side: category PNG icon on cream background

import React from 'react'
import type { MachineInstance } from '../../types/machineInstance'
import {
  getMachineStatusColor,
  getMachineStatusLabel,
  formatHorimeterHours,
  calculateMaintenanceStatus,
} from '../../types/machineInstance'
import { MACHINE_CATEGORIES, getMachineIcon } from '../../types/machine'
import { AlertTriangle } from 'lucide-react'

// AGC: Normalize English categories from legacy Firestore data for display lookup
const CATEGORY_NORMALIZE: Record<string, string> = {
  tractor: 'trator', harvester: 'colheitadeira', sprayer: 'pulverizador',
  planter: 'plantadeira', spreader: 'distribuidor', implement: 'implemento',
  truck: 'transporte', drone: 'outros',
}

interface MachineCardProps {
  machine: MachineInstance
  onClick: () => void
  imageUrl?: string | null
  isImageLoading?: boolean
}

export const MachineCard: React.FC<MachineCardProps> = ({
  machine,
  onClick,
}) => {
  const machineWithStatus = calculateMaintenanceStatus(machine)
  const { maintenanceStatus } = machineWithStatus

  const normalizedCategory = CATEGORY_NORMALIZE[machine.category?.toLowerCase()] || machine.category
  const categoryInfo = MACHINE_CATEGORIES.find((c) => c.id === normalizedCategory)
  const categoryName = categoryInfo?.name || machine.category
  const categoryImg = getMachineIcon(machine.category, machine.brand, machine.specs, machine.customIconPath, machine.name, machine.brandColor)

  const statusColor = getMachineStatusColor(machine.status)
  const statusLabel = getMachineStatusLabel(machine.status)
  const hoursDisplay = formatHorimeterHours(machine.horimeterHours)
  const showMaintenanceAlert = maintenanceStatus === 'due_soon' || maintenanceStatus === 'overdue'

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-2xl overflow-hidden transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] border border-gray-100 dark:border-gray-700 bg-[#deebe1] dark:bg-gray-800 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.3)] flex"
    >
      {/* AGC: Left — machine info */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        {/* AGC: Status + maintenance alert */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold text-white ${statusColor} rounded-full`}>
            {statusLabel}
          </span>
          {showMaintenanceAlert && (
            <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-semibold text-white rounded-full ${
              maintenanceStatus === 'overdue' ? 'bg-red-500' : 'bg-yellow-500'
            }`}>
              <AlertTriangle className="w-2.5 h-2.5" />
              {maintenanceStatus === 'overdue' ? 'Atrasada' : 'Próxima'}
            </span>
          )}
        </div>

        {/* AGC: Machine name */}
        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight truncate mt-2">
          {machine.name}
        </h3>

        {/* AGC: Category + brand/model */}
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate mt-0.5">
          {categoryName}
          {machine.brand && ` · ${machine.brand}`}
          {machine.model && ` ${machine.model}`}
        </p>

        {/* AGC: Horimeter */}
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
          Horímetro: {hoursDisplay}
        </p>
      </div>

      {/* AGC: Right — category icon */}
      <div className="w-[130px] flex-shrink-0 flex items-center justify-center p-1">
        {categoryImg ? (
          <img src={categoryImg} alt={categoryName} className="w-full h-full object-contain max-h-[100px]" />
        ) : (
          <span className="text-5xl opacity-30">{categoryInfo?.icon || '🚜'}</span>
        )}
      </div>
    </button>
  )
}

export default MachineCard
